import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendar, FaUser, FaArrowLeft, FaClock } from 'react-icons/fa';
import { useWordPress } from '../hooks/useWordPress';
import SubscribeForm from '../components/SubscribeForm';
import './BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const { usePost } = useWordPress();
  const { data: post, isLoading, error } = usePost(slug);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(Math.min(progress, 100));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    return (
      <div className="blog-post-page">
        <div className="reading-progress" style={{ width: '0%' }} />
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <div className="error-container">
            <h1>Post Not Found</h1>
            <p>The post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="btn btn-primary">
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const featuredImage = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
  const author = post._embedded?.author?.[0]?.name || 'Unknown Author';
  const categories = post._embedded?.['wp:term']?.[0] || [];
  const readTime = Math.ceil(post.content.rendered.split(' ').length / 200);

  return (
    <div className="blog-post-page">
      {/* Reading progress bar */}
      <div className="reading-progress" style={{ width: `${scrollProgress}%` }} />

      <div className="container">
        {/* Breadcrumb back link */}
        <div className="back-link">
          <Link to="/blog" className="back-btn">
            <FaArrowLeft />
            Back to Blog
          </Link>
        </div>

        <motion.article
          className="blog-post"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <header className="post-header">
            {categories.length > 0 && (
              <div className="post-categories">
                {categories.map(category => (
                  <Link
                    key={category.id}
                    to={`/blog?category=${category.id}`}
                    className="category-tag"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            )}

            <h1 className="post-title" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />

            <div className="post-meta">
              <span className="post-date">
                <FaCalendar />
                {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span className="post-author">
                <FaUser />
                {author}
              </span>
              <span className="post-read-time">
                <FaClock />
                {readTime} min read
              </span>
            </div>
          </header>

          {featuredImage && (
            <div className="post-featured-image">
              <img src={featuredImage} alt={post.title.rendered} />
            </div>
          )}

          <div
            className="post-content"
            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
          />

          <SubscribeForm variant="post" />
        </motion.article>
      </div>
    </div>
  );
};

export default BlogPostPage;
